import { useCallback, useEffect, useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';

//components
import withLocalization from '../../../../hoc/withLocalization';
import Button from '../../../../components/CustomButton/CustomButton';
import PermissionGroupList from './PermissionGroupList';
import PermissionGroupMatrix from './PermissionGroupMatrix';
import PermissionGroupProfile from './PermissionGroupProfile';
import PermissionGroupMembers from './PermissionGroupMembers';

//elements
import LoadingSpinner from '../../../../elements/LoadingSpinner';
import RequiredStar from '../../../../elements/RequiredStar';

//utils
import { shortDateFormat } from '../../../../utils/dateFormat';
import {
    GROUP_ROLES,
    buildDepartmentLabels,
    buildGeneralModules,
    buildRosterModules,
    buildGroupModules,
    buildEmptyPermissionMap,
    applyPermissionChange,
    applyModuleToggle,
    applyGeneralColumnToggle,
    countGranted,
    countTotal,
    countProfileFields,
    countManagedProfileFields,
    getProfileFieldErrors,
} from './permissionGroupUtils';

//styles
import '../styles.css';
import '../../Roster/RosterScheduler/css/workPatternStyle.css';
import '../../../../components/Chat/ConversationSearch/ConversationSearch.css';

const NEW_GROUP_ID = 'new';

// the panel is a fixed-height column, so this is about what fits without scrolling
const GROUPS_PER_PAGE = 8;

const buildDraft = (group) => JSON.parse(JSON.stringify(group));

const PermissionGroups = ({ t, permissionGroupStore, commonStore }) => {
    const clientData = commonStore?.config?.client?.data;
    const clientAllowsCompanyShifts = !!commonStore?.config?.client?.allow_company_shifts;
    // read the observables out here so the memo recomputes when they load - eslint
    // cannot see that a MobX observable on a stable store reference has changed
    const weeklyOvertimeRules = commonStore.weeklyOvertimeSettings;
    const overtimeCapRules = commonStore.weeklyOvertimeCapSettings;
    // `t` is a stable function, so read the observable locale to re-render (and
    // recompute translated memos) when the language is switched
    const locale = commonStore.locale;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const departmentLabels = useMemo(() => buildDepartmentLabels(clientData, t), [clientData, t, locale]);
    const flags = useMemo(() => {
        const basicRules = clientData?.basicRules || {};
        const loginRules = clientData?.loginRules || {};
        const overtimeBankSettings = basicRules.overtimeBankSettings || {};
        const allowCompanyShifts = clientAllowsCompanyShifts;
        return {
            rosterEnabled: !!basicRules.workPlanSettings?.allow_work_hours_settings,
            allowMembersToManageScheduleShift: !!basicRules.allowMembersToManageScheduleShift,
            allowShiftAutoTimesheetToggle: !!loginRules.allowShiftToEnableDisableAutoTimesheet,
            // gates for the onboarding profile fields, mirroring library/schemas/user.js
            allowCompanyShifts,
            allowResourcePlanner: !!basicRules.workPlanSettings?.allow_resource_planner,
            allowAutomaticFlexitime: !!(allowCompanyShifts && basicRules.timeBankSettings?.allowAutomaticFlexitimeTimeBank),
            allowUserAutoTimesheetToggle: loginRules.autoTimelogs === 'autoschedule'
                && !!loginRules.allowUserToEnableDisableAutoTimesheet,
            // the per-employee toggle only exists while mobile apps are blocked
            // company-wide - it is the exception to that block
            mobileAccessBlocked: !!loginRules.disableMobileAccess,
            paidOvertimeRenewalConfigured: !!(overtimeBankSettings.allowOvertimeBank
                && overtimeBankSettings.allowPaidOvertimeBalanceManagement
                && overtimeBankSettings.autoOvertimeBankRenewal),
            // company shifts gate overtime rules altogether (same condition as the
            // per-user overtime action); beyond that, only hide a picker when the
            // company has no rules at all - inactive ones are still shown disabled
            hasWeeklyOvertimeRules: clientAllowsCompanyShifts && !!(weeklyOvertimeRules || []).length,
            hasOvertimeCapRules: clientAllowsCompanyShifts && !!(overtimeCapRules || []).length,
        };
    }, [clientData, clientAllowsCompanyShifts, weeklyOvertimeRules, overtimeCapRules]);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [groups, setGroups] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [draft, setDraft] = useState(null);
    // `search` is what is typed; `appliedSearch` is what the server was asked for,
    // because the project's search applies on Enter rather than per keystroke
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [detailTab, setDetailTab] = useState('permissions');
    const [assignableUsers, setAssignableUsers] = useState({ pm: [], member: [] });

    /**
     * Reloads the current page of groups and the assignable employee lists; keeps
     * `keepId` selected when that group is still on the page. Filtering and paging
     * are the server's job, so every one of them comes back through here.
     */
    const loadGroups = useCallback(async (keepId, query = {}) => {
        const wanted = {
            page: query.page ?? page,
            name: query.name ?? appliedSearch,
            user_type: query.user_type ?? roleFilter,
        };
        setLoading(true);
        try {
            const [list, pmUsers, memberUsers] = await Promise.all([
                permissionGroupStore.getGroups({
                    page: wanted.page - 1,
                    pagesize: GROUPS_PER_PAGE,
                    name: wanted.name || undefined,
                    user_type: wanted.user_type || undefined,
                }),
                permissionGroupStore.getAssignableUsers('pm'),
                permissionGroupStore.getAssignableUsers('member'),
                // the Overtime rules section picks from the company's rule lists
                commonStore.loadUserOvertimeSettingOptions(),
            ]);
            setAssignableUsers({ pm: pmUsers, member: memberUsers });
            setGroups(list.groups);
            setPages(list.pages);
            setTotal(list.total);
            // the last group on a page can be deleted out from under us
            if (wanted.page > list.pages) return void setPage(list.pages);
            setPage(wanted.page);
            const next = list.groups.find(group => group.id === keepId) || list.groups[0] || null;
            setSelectedId(next ? next.id : null);
            setDraft(next ? buildDraft(next) : null);
            setDetailTab('permissions');
        } catch (err) {
            commonStore.addNotification(err.message || t('Something went wrong'), null, 'error');
            console.error(err);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permissionGroupStore, commonStore, t, page, appliedSearch, roleFilter]);

    // only on mount - every later reload is triggered explicitly
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { loadGroups(); }, []);

    const savedGroup = useMemo(
        () => groups.find(group => group.id === selectedId) || null,
        [groups, selectedId]
    );

    const isNew = selectedId === NEW_GROUP_ID;

    /**
     * The definition (name, role, permissions, onboarding fields) and the employee
     * list are saved by two separate buttons, so each tracks its own unsaved state.
     */
    const definitionDirty = useMemo(() => {
        if (!draft) return false;
        if (isNew) return true;
        return JSON.stringify({
            name: savedGroup?.name,
            description: savedGroup?.description,
            user_type: savedGroup?.user_type,
            permissions: savedGroup?.permissions,
            profile_settings: savedGroup?.profile_settings,
        }) !== JSON.stringify({
            name: draft.name,
            description: draft.description,
            user_type: draft.user_type,
            permissions: draft.permissions,
            profile_settings: draft.profile_settings,
        });
    }, [draft, savedGroup, isNew]);

    const membersDirty = useMemo(() => {
        if (!draft || isNew) return false;
        const ids = list => (list || []).map(member => member.id).sort();
        return JSON.stringify(ids(savedGroup?.members)) !== JSON.stringify(ids(draft.members));
    }, [draft, savedGroup, isNew]);

    const isDirty = definitionDirty || membersDirty;

    /**
     * All three tabs edit one draft and are saved by one button, so each tab has
     * to say when it is holding unsaved edits - otherwise an admin who changes
     * permissions, switches to Employees and adds people has no way to see that
     * the permission change is still pending and will be applied along with it.
     */
    const pendingTabs = useMemo(() => {
        if (!draft) return {};
        const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
        if (isNew) {
            // nothing saved yet, so flag whatever has been filled in so far
            return {
                permissions: countGranted(draft.permissions) > 0,
                profile: countManagedProfileFields(draft.profile_settings, flags) > 0,
                members: false,
            };
        }
        return {
            permissions: !same(savedGroup?.permissions, draft.permissions),
            profile: !same(savedGroup?.profile_settings, draft.profile_settings),
            members: membersDirty,
        };
    }, [draft, savedGroup, isNew, flags, membersDirty]);

    const modules = useMemo(
        () => (draft ? buildGroupModules(draft.user_type, flags) : []),
        [draft, flags]
    );
    const generalModules = useMemo(
        () => (draft ? buildGeneralModules(draft.user_type) : []),
        [draft]
    );
    const rosterModules = useMemo(
        () => (draft ? buildRosterModules(draft.user_type, flags) : []),
        [draft, flags]
    );

    /** userId -> group id, used to block an employee from sitting in two groups */
    const membershipByUserId = useMemo(() => {
        const map = {};
        [...assignableUsers.pm, ...assignableUsers.member].forEach(user => {
            if (user.permission_group_id) map[user.id] = user.permission_group_id;
        });
        return map;
    }, [assignableUsers]);

    const roleUsers = useMemo(
        () => (draft ? assignableUsers[draft.user_type] || [] : []),
        [draft, assignableUsers]
    );

    const employeeOptions = useMemo(
        // groupName rides along because the group an employee already follows may
        // not be on the page of groups currently loaded
        () => roleUsers.map(user => ({
            value: user.id,
            label: user.fullname,
            groupName: user.permission_group_name || null,
        })),
        [roleUsers]
    );

    /**
     * Overtime rules are appended, so an employee already on an active rule keeps
     * it instead of being moved. The server names them - pass that on rather than
     * letting the admin assume everyone was switched over.
     */
    const reportSkippedOvertimeRules = (skipped) => {
        if (!skipped || !skipped.length) return;
        const names = [...new Set(skipped.map(skip => skip.fullname))];
        commonStore.addNotification(
            `${names.join(', ')} - ${t('kept their existing overtime rule')}`,
            null, 'warning', 'top-right', 5000
        );
    };

    const withDirtyGuard = (proceed) => {
        if (!isDirty) return proceed();
        return confirmAlert({
            title: t('Confirm discard'),
            message: t('You have unsaved permission changes that will be lost. Continue?'),
            buttons: [
                { label: t('Cancel'), onClick: () => { } },
                { label: t('Discard Changes'), onClick: proceed },
            ],
        });
    };

    const handlePageChange = (nextPage) => {
        if (nextPage === page) return;
        withDirtyGuard(() => { setPage(nextPage); loadGroups(selectedId, { page: nextPage }); });
    };

    const handleSearchSubmit = () => {
        const term = search.trim();
        if (term === appliedSearch) return;
        withDirtyGuard(() => {
            setAppliedSearch(term);
            setPage(1);
            loadGroups(selectedId, { page: 1, name: term });
        });
    };

    const handleRoleFilterChange = (value) => {
        if (value === roleFilter) return;
        withDirtyGuard(() => {
            setRoleFilter(value);
            setPage(1);
            loadGroups(selectedId, { page: 1, user_type: value });
        });
    };

    const handleSelect = (id) => {
        if (id === selectedId) return;
        withDirtyGuard(() => {
            const group = groups.find(group => group.id === id);
            if (!group) return;
            setSelectedId(id);
            setDraft(buildDraft(group));
            setDetailTab('permissions');
        });
    };

    const handleCreate = () => {
        withDirtyGuard(() => {
            const role = flags.rosterEnabled ? 'pm' : 'member';
            setSelectedId(NEW_GROUP_ID);
            setDraft({
                id: NEW_GROUP_ID,
                name: '',
                description: '',
                user_type: role,
                permissions: buildEmptyPermissionMap(role, flags),
                profile_settings: {},
                members: [],
            });
            setDetailTab('permissions');
        });
    };

    const handleDuplicate = () => {
        if (!draft) return;
        withDirtyGuard(() => {
            setSelectedId(NEW_GROUP_ID);
            setDraft({
                ...buildDraft(draft),
                id: NEW_GROUP_ID,
                name: `${draft.name} ${t('Copy')}`,
                members: [],
            });
            setDetailTab('permissions');
        });
    };

    const handleRoleChange = (role) => {
        if (!draft || draft.user_type === role) return;
        // profile settings are the same for both roles, so only permissions reset
        const applyRole = () => setDraft(prev => ({
            ...prev,
            user_type: role,
            permissions: buildEmptyPermissionMap(role, flags),
            members: [],
        }));
        if (!draft.members.length) return applyRole();
        confirmAlert({
            title: t('Change group type?'),
            message: t('Changing the group type clears its permissions and assigned employees. Continue?'),
            buttons: [
                { label: t('Cancel'), onClick: () => { } },
                { label: t('Continue'), onClick: applyRole },
            ],
        });
    };

    const handlePermissionChange = (moduleKey, permValue, value) => {
        setDraft(prev => ({
            ...prev,
            permissions: applyPermissionChange(prev.permissions, modules, moduleKey, permValue, value),
        }));
    };

    const handleModuleToggle = (moduleKey, value) => {
        setDraft(prev => ({ ...prev, permissions: applyModuleToggle(prev.permissions, modules, moduleKey, value) }));
    };

    const handleProfileChange = (profileSettings) => {
        setDraft(prev => ({ ...prev, profile_settings: profileSettings }));
    };

    const handleGeneralColumnToggle = (permValue, value) => {
        setDraft(prev => ({ ...prev, permissions: applyGeneralColumnToggle(prev.permissions, modules, permValue, value) }));
    };

    const addMembers = (userIds) => {
        const moving = userIds.filter(id => membershipByUserId[id] && membershipByUserId[id] !== selectedId);
        const commit = () => setDraft(prev => ({
            ...prev,
            members: [
                ...prev.members,
                ...userIds.map(id => {
                    const user = roleUsers.find(candidate => candidate.id === id);
                    return {
                        id,
                        fullname: user?.fullname || `#${id}`,
                        user_type: user?.user_type || prev.user_type,
                        employee_type: user?.employee_type || 'department',
                        is_customised: false,
                    };
                }),
            ],
        }));
        if (!moving.length) return commit();
        confirmAlert({
            title: t('Move employees to this permission group?'),
            message: `${moving.length} ${t('of the selected employees already belong to another permission group. They will be moved here and their permissions replaced. Continue?')}`,
            buttons: [
                { label: t('Cancel'), onClick: () => { } },
                { label: t('Move And Replace'), onClick: commit },
            ],
        });
    };

    // takes a single id from the row button, or a list from the bulk selection
    const removeMembers = (userId) => {
        const ids = Array.isArray(userId) ? userId : [userId];
        if (!ids.length) return;
        const removing = new Set(ids);
        confirmAlert({
            title: ids.length > 1 ? t('Remove employees from group?') : t('Remove from group?'),
            message: ids.length > 1
                ? `${ids.length} ${t('selected employees keep the permissions they have now, but will no longer follow this permission group.')}`
                : t('The employee keeps the permissions they have now, but will no longer follow this permission group.'),
            buttons: [
                { label: t('Cancel'), onClick: () => { } },
                {
                    label: t('Remove'),
                    onClick: () => setDraft(prev => ({ ...prev, members: prev.members.filter(m => !removing.has(m.id)) })),
                },
            ],
        });
    };

    const reapplyToMember = (userId) => {
        if (isNew || isDirty)
            return commonStore.addNotification(t('Please save the permission group before re-applying it'), null, 'warning');
        confirmAlert({
            title: t('Re-apply group permissions'),
            message: t('The individual permission changes made for this employee will be replaced by the group permissions. Continue?'),
            buttons: [
                { label: t('Cancel'), onClick: () => { } },
                {
                    label: t('Re-apply'),
                    onClick: async () => {
                        setSaving(true);
                        try {
                            const response = await permissionGroupStore.applyGroup(selectedId, [userId]);
                            commonStore.addNotification(t('Saved'), null, 'success');
                            reportSkippedOvertimeRules(response?.skipped);
                            await loadGroups(selectedId);
                        } catch (err) {
                            commonStore.addNotification(err.message || t('Something went wrong'), null, 'error');
                        } finally {
                            setSaving(false);
                        }
                    },
                },
            ],
        });
    };

    const handleDelete = () => {
        if (!savedGroup) return;
        confirmAlert({
            title: t('Delete permission group?'),
            message: (savedGroup.members || []).length
                ? `${t('This permission group is assigned to')} ${savedGroup.members.length} ${t('employees. They keep their current permissions but stop following the permission group.')}`
                : t('This permission group will be removed permanently.'),
            buttons: [
                { label: t('Cancel'), onClick: () => { } },
                {
                    label: t('Delete'),
                    onClick: async () => {
                        setSaving(true);
                        try {
                            await permissionGroupStore.removeGroup(savedGroup.id);
                            commonStore.addNotification(t('Deleted'), null, 'success');
                            await loadGroups();
                        } catch (err) {
                            commonStore.addNotification(err.message || t('Something went wrong'), null, 'error');
                        } finally {
                            setSaving(false);
                        }
                    },
                },
            ],
        });
    };

    /** Discards only the part being edited, matching the split save buttons. */
    const handleDiscard = () => {
        if (isNew) {
            return withDirtyGuard(() => {
                setSelectedId(groups.length ? groups[0].id : null);
                setDraft(groups.length ? buildDraft(groups[0]) : null);
            });
        }
        if (onMembersTab) {
            return setDraft(prev => ({ ...prev, members: buildDraft(savedGroup).members }));
        }
        const restored = buildDraft(savedGroup);
        setDraft(prev => ({
            ...restored,
            // keep any unsaved employee edits, they belong to the other button
            members: prev.members,
        }));
    };

    /**
     * Saves the group definition. Membership is untouched, but the employees
     * already in the group pick up the new permissions - that is what being in a
     * group means, so it is confirmed when the group is not empty.
     */
    const handleSaveDefinition = () => {
        if (!draft) return;
        if (!draft.name || !draft.name.trim())
            return commonStore.addNotification(t('Please enter name'), null, 'warning');
        // only catches a clash on the page in front of the admin - the server owns
        // the real check, and its error surfaces the same message
        if (groups.some(group => group.id !== draft.id && group.name.trim().toLowerCase() === draft.name.trim().toLowerCase()))
            return commonStore.addNotification(t('A permission group with this name already exists'), null, 'warning');

        // field errors are shown under each control on the Profile Settings tab
        // (same as the user profile form), so just take the admin there
        if (Object.keys(getProfileFieldErrors(draft.profile_settings, flags)).length)
            return setDetailTab('profile');

        const proceed = async () => {
            setSaving(true);
            try {
                const response = await permissionGroupStore.saveGroup(draft);
                commonStore.addNotification(t('Saved'), null, 'success');
                reportSkippedOvertimeRules(response?.skipped);
                await loadGroups(response?.id || (isNew ? undefined : draft.id));
            } catch (err) {
                commonStore.addNotification(err.message || t('Something went wrong'), null, 'error');
            } finally {
                setSaving(false);
            }
        };

        const alreadyIn = (savedGroup?.members || []).length;
        if (isNew || !alreadyIn) return proceed();
        confirmAlert({
            title: t('Confirm Update'),
            message: `${t('Updating permissions for')} ${alreadyIn} ${t('user(s)')} ${t('already in this permission group')}. ${t('Their existing individual permissions will be replaced by this permission group')}. ${t('Continue')}?`,
            buttons: [
                { label: t('Yes'), onClick: proceed },
                { label: t('No'), onClick: () => { } },
            ],
        });
    };

    /** Saves the employee list on its own; the group definition is untouched. */
    const handleSaveMembers = () => {
        if (!draft || isNew) return;
        const savedIds = new Set((savedGroup?.members || []).map(member => member.id));
        const draftIds = new Set(draft.members.map(member => member.id));
        const added = draft.members.filter(member => !savedIds.has(member.id));
        const removed = (savedGroup?.members || []).filter(member => !draftIds.has(member.id));

        const proceed = async () => {
            setSaving(true);
            try {
                const response = await permissionGroupStore.saveMembers(selectedId, [...draftIds]);
                commonStore.addNotification(t('Saved'), null, 'success');
                reportSkippedOvertimeRules(response?.skipped);
                await loadGroups(selectedId);
            } catch (err) {
                commonStore.addNotification(err.message || t('Something went wrong'), null, 'error');
            } finally {
                setSaving(false);
            }
        };

        if (!added.length) return proceed();
        const removedNote = removed.length ? ` ${removed.length} ${t('will stop following it and keep their current permissions')}.` : '';
        confirmAlert({
            title: t('Confirm Update'),
            message: `${added.length} ${t('user(s)')} ${t('will be added to this permission group')}. ${t('Their existing individual permissions will be replaced by this permission group')}.${removedNote} ${t('Continue')}?`,
            buttons: [
                { label: t('Yes'), onClick: proceed },
                { label: t('No'), onClick: () => { } },
            ],
        });
    };

    if (loading) return <LoadingSpinner />;

    // the footer acts on whichever part is on screen: the Employees tab saves the
    // employee list, the other two save the group definition
    const onMembersTab = detailTab === 'members';
    const activeDirty = onMembersTab ? membersDirty : definitionDirty;

    const grantedCount = draft ? countGranted(draft.permissions) : 0;
    const totalCount = draft ? countTotal(draft.user_type, flags) : 0;
    const profileManagedCount = draft ? countManagedProfileFields(draft.profile_settings, flags) : 0;
    const profileTotalCount = countProfileFields(flags, draft?.profile_settings);
    const profileHasErrors = draft ? Object.keys(getProfileFieldErrors(draft.profile_settings, flags)).length > 0 : false;

    return (
        <Row className="gx-3">
            <Col xs={12} lg={4} xl={3} className="mb-3 mb-lg-0">
                <PermissionGroupList
                    t={t}
                    groups={groups}
                    selectedId={selectedId}
                    search={search}
                    roleFilter={roleFilter}
                    isDirty={isDirty}
                    page={page}
                    pages={pages}
                    total={total}
                    onSearchChange={setSearch}
                    onSearchSubmit={handleSearchSubmit}
                    onRoleFilterChange={handleRoleFilterChange}
                    onPageChange={handlePageChange}
                    onSelect={handleSelect}
                    onCreate={handleCreate}
                />
            </Col>

            <Col xs={12} lg={8} xl={9}>
                {!draft ? (
                    <div className="card custom-card">
                        <div className="card-body d-flex flex-column align-items-center justify-content-center text-center py-5">
                            <h5 className="fw-bold">{t('No permission group selected')}</h5>
                            <p className="font-size-12 text-dark mb-3">
                                {t('Create a permission group, set the permissions and onboarding fields once, then assign employees to it. Every assigned employee gets exactly these settings.')}
                            </p>
                            <Button fill wd onClick={handleCreate}>
                                <i className="fa fa-plus-circle me-2" aria-hidden="true" />{t('New')} {t('Group')}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="card custom-card">
                        <div className="card-header bg-light">
                            <Row className="gx-2 align-items-start">
                                <Col xs={12} md={5} className="mb-2 mb-md-0">
                                    <label className="control-label text-dark">
                                        {t('Group Name')}<RequiredStar />
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder={t('Enter Name')}
                                        value={draft.name}
                                        onChange={e => setDraft(prev => ({ ...prev, name: e.target.value }))}
                                    />
                                </Col>
                                <Col xs={12} md={4} className="mb-2 mb-md-0">
                                    <label className="control-label text-dark">{t('Description')}</label>
                                    <textarea
                                        className="form-control form-control-sm perm-group-description"
                                        rows={3}
                                        maxLength={255}
                                        placeholder={t('Short description')}
                                        value={draft.description || ''}
                                        onChange={e => setDraft(prev => ({ ...prev, description: e.target.value }))}
                                    />
                                </Col>
                                <Col xs={12} md={3}>
                                    <label className="control-label text-dark">
                                        {t('Applies to')}
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip id="tip-role">{t('A permission group targets one user type, because project managers and members have different permission modules.')}</Tooltip>}
                                        >
                                            <i className="fa fa-info-circle text-muted opacity-50 ms-1" aria-hidden="true" />
                                        </OverlayTrigger>
                                    </label>
                                    <div className="d-flex">
                                        {GROUP_ROLES.map((role, index) => (
                                            <button
                                                key={role.value}
                                                type="button"
                                                className={`tab-btn perm-group-filter-btn${index === 0 ? ' tab-btn-left' : ' tab-btn-right'}${draft.user_type === role.value ? ' active' : ''}`}
                                                onClick={() => handleRoleChange(role.value)}
                                            >
                                                {t(role.label)}
                                            </button>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        <div className="card-body pt-2">
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                                <div className="d-flex flex-wrap perm-group-tabs">
                                    <button
                                        type="button"
                                        className={`tab-btn tab-btn-left${detailTab === 'permissions' ? ' active' : ''}`}
                                        onClick={() => setDetailTab('permissions')}
                                        title={pendingTabs.permissions ? t('Unsaved changes') : undefined}
                                    >
                                        {t('Permissions')} ({grantedCount}/{totalCount})
                                        {pendingTabs.permissions ? <i className="fa fa-circle fa-xs ms-2 text-danger" aria-hidden="true" /> : null}
                                    </button>
                                    <button
                                        type="button"
                                        className={`tab-btn${detailTab === 'profile' ? ' active' : ''}`}
                                        onClick={() => setDetailTab('profile')}
                                        title={pendingTabs.profile ? t('Unsaved changes') : undefined}
                                    >
                                        {t('Profile Settings')} ({profileManagedCount}/{profileTotalCount})
                                        {profileHasErrors
                                            ? <i className="fa fa-exclamation-circle ms-2 text-danger" aria-hidden="true" />
                                            : pendingTabs.profile ? <i className="fa fa-circle fa-xs ms-2 text-danger" aria-hidden="true" /> : null}
                                    </button>
                                    <button
                                        type="button"
                                        className={`tab-btn tab-btn-right${detailTab === 'members' ? ' active' : ''}`}
                                        onClick={() => setDetailTab('members')}
                                        disabled={isNew}
                                        title={isNew
                                            ? t('Save the permission group first, then assign employees')
                                            : (pendingTabs.members ? t('Unsaved changes') : undefined)}
                                    >
                                        {t('Assign Employees')} ({draft.members.length})
                                        {pendingTabs.members ? <i className="fa fa-circle fa-xs ms-2 text-danger" aria-hidden="true" /> : null}
                                    </button>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    {isNew ? (
                                        <span className="font-size-12 text-dark me-2">
                                            <i className="fa fa-info-circle me-1" aria-hidden="true" />
                                            {t('Save the permission group first, then assign employees')}
                                        </span>
                                    ) : null}
                                    {!isNew ? (
                                        <>
                                            <OverlayTrigger placement="top" overlay={<Tooltip id="tip-duplicate">{t('Duplicate this permission group')}</Tooltip>}>
                                                <Button icon_sm fill onClick={handleDuplicate}>
                                                    <i className="fa fa-clone" aria-hidden="true" />
                                                </Button>
                                            </OverlayTrigger>
                                            <OverlayTrigger placement="top" overlay={<Tooltip id="tip-delete">{t('Delete')}</Tooltip>}>
                                                <Button icon_sm_delete fill onClick={handleDelete}>
                                                    <i className="fa fa-trash" aria-hidden="true" />
                                                </Button>
                                            </OverlayTrigger>
                                        </>
                                    ) : (
                                        <span className="badge bg-warning text-dark">{t('Not saved yet')}</span>
                                    )}
                                </div>
                            </div>

                            {detailTab === 'profile' ? (
                                <PermissionGroupProfile
                                    profileSettings={draft.profile_settings || {}}
                                    flags={flags}
                                    onChange={handleProfileChange}
                                />
                            ) : detailTab === 'permissions' ? (
                                <PermissionGroupMatrix
                                    t={t}
                                    role={draft.user_type}
                                    generalModules={generalModules}
                                    rosterModules={rosterModules}
                                    permissionMap={draft.permissions}
                                    readOnly={false}
                                    onPermissionChange={handlePermissionChange}
                                    onModuleToggle={handleModuleToggle}
                                    onGeneralColumnToggle={handleGeneralColumnToggle}
                                />
                            ) : (
                                <PermissionGroupMembers
                                    t={t}
                                    locale={locale}
                                    role={draft.user_type}
                                    members={draft.members}
                                    employeeOptions={employeeOptions}
                                    membershipByUserId={membershipByUserId}
                                    departmentLabels={departmentLabels}
                                    groupId={selectedId}
                                    onAdd={addMembers}
                                    onRemove={removeMembers}
                                    onReapply={reapplyToMember}
                                />
                            )}
                        </div>

                        <div className="card-footer bg-light perm-group-action-bar d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <span className="font-size-12 text-dark">
                                {activeDirty ? (
                                    <>
                                        <i className="fa fa-circle fa-xs text-danger me-1" aria-hidden="true" />
                                        {t('Unsaved changes')}
                                    </>
                                ) : savedGroup?.updated_at ? (
                                    <>{t('Last updated')} : {shortDateFormat(savedGroup.updated_at, commonStore.getDateTimeRules())}{savedGroup.updated_by ? ` · ${savedGroup.updated_by}` : ''}</>
                                ) : null}
                            </span>
                            <div className="d-flex align-items-center gap-2">
                                <Button wd onClick={handleDiscard} disabled={!activeDirty || saving}>{t('Discard')}</Button>
                                {onMembersTab ? (
                                    <Button fill wd onClick={handleSaveMembers} disabled={!membersDirty || saving}>
                                        {saving ? t('Saving...') : `${t('Save')} ${t('Employees')}`}
                                    </Button>
                                ) : (
                                    <Button fill wd onClick={handleSaveDefinition} disabled={!definitionDirty || saving}>
                                        {saving ? t('Saving...') : `${t('Save')} ${t('Group')}`}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Col>
        </Row>
    );
};

export default inject('permissionGroupStore', 'commonStore')(withLocalization(observer(PermissionGroups)));
